import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Send, Sunrise } from "lucide-react";

/** Admin: global switch + hour for the daily "Mi Escritorio" digest email, with manual triggers. */
export function DigestSettings({ t }) {
  const [st, setSt] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/digest/settings").then((r) => setSt(r.data)).catch(() => setSt(false));
    api.get("/digest/runs").then((r) => setRuns(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async (patch) => {
    try {
      const { data } = await api.put("/digest/settings", patch);
      setSt((s) => ({ ...s, ...data }));
      toast.success(t("common.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendMe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digest/send-me");
      toast.success(data.status === "sent" ? `${t("set.digestSentMe")} (${data.total})` : t("set.digestEmpty"));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const sendAll = async () => {
    if (!window.confirm(t("set.digestConfirmAll"))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(`${t("set.digestSentAll")}: ${data.sent} · ${t("set.digestSkipped")}: ${data.skipped_empty} · ${t("set.bulkErrors")}: ${data.errors}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (st === false) return null;
  if (!st) return null;

  return (
    <div className="border bg-card p-5 mb-4" data-testid="digest-settings-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1 flex items-center gap-1"><Sunrise size={12} /> {t("set.digest")}</div>
          <div className="text-[11px] text-muted-foreground">{t("set.digestSubtitle")}</div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={!!st.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="digest-enabled-switch" />
          <span className="text-xs font-bold">{st.enabled ? t("common.active") : t("common.inactive")}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-6 items-end">
        <div className="space-y-1">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.digestHour")}</Label>
          <div className="flex items-center gap-1">
            <Input type="number" min="0" max="23" value={st.hour_local}
              onChange={(e) => setSt({ ...st, hour_local: Number(e.target.value) })}
              onBlur={() => save({ hour_local: Number(st.hour_local) })}
              className="rounded-none h-8 text-xs w-20" data-testid="digest-hour-input" />
            <span className="text-[10px] text-muted-foreground">:00 {st.timezone}</span>
          </div>
          <p className="text-[9px] text-muted-foreground">{t("set.digestHourHint")}</p>
        </div>
        <div className="text-xs">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("set.digestRecipients")}</div>
          <div className="font-black tabular-nums text-lg" data-testid="digest-recipients-count">{st.recipients_count}</div>
        </div>
        <div className="text-xs">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("mrp.lastRun")}</div>
          <div data-testid="digest-last-run">{st.last_run ? new Date(st.last_run).toLocaleString("es-MX") : "—"}</div>
          {st.last_stats?.users != null && (
            <div className="text-[10px] text-muted-foreground">{st.last_stats.sent} {t("set.digestSentLbl")} · {st.last_stats.skipped_empty} {t("set.digestSkipped")} · {st.last_stats.errors} err</div>
          )}
        </div>
        <div className="flex gap-2 ml-auto">
          <Button size="sm" variant="outline" className="rounded-none text-xs h-8" disabled={busy} onClick={sendMe} data-testid="digest-send-me-btn">
            <Send size={12} className="mr-1" /> {t("set.digestSendMe")}
          </Button>
          <Button size="sm" className="rounded-none text-xs h-8" disabled={busy} onClick={sendAll} data-testid="digest-send-all-btn">
            <Send size={12} className="mr-1" /> {t("set.digestSendAll")}
          </Button>
        </div>
      </div>
      {runs.length > 0 && (
        <div className="tbl-wrap mt-3" data-testid="digest-runs-table">
          <table className="tbl">
            <thead><tr><th>{t("common.date")}</th><th className="num">{t("set.digestRecipients")}</th><th className="num">{t("set.digestSentLbl")}</th><th className="num">{t("set.digestSkipped")}</th><th className="num">Err</th></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="text-muted-foreground">{new Date(r.run_at).toLocaleString("es-MX")}</td>
                  <td className="num">{r.stats?.users}</td><td className="num">{r.stats?.sent}</td>
                  <td className="num">{r.stats?.skipped_empty}</td><td className="num">{r.stats?.errors}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
